import {
  Component,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DobryAgentFacilityService } from '../../services/dobry-agent-facility.service';
import { SubCategory } from '../../interface/facility-dobry-agent';

@Component({
  selector: 'app-sub-category-dobry-agent-select',
  templateUrl: './sub-category-dobry-agent-select.component.html',
  styleUrls: ['./sub-category-dobry-agent-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SubCategoryDobryAgentSelectComponent),
      multi: true,
    },
  ],
})
export class SubCategoryDobryAgentSelectComponent implements OnInit, OnDestroy, ControlValueAccessor {
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  @Input() label = 'Podkategorie';
  @Input() placeholder = 'Wybierz podkategorie';
  @Input() multiple = true;
  @Input() required = false;
  @Input() onlyActive = true;

  items: SubCategory[] = [];
  filteredItems: SubCategory[] = [];

  value: string | string[] | null = [];
  disabled = false;

  private onChange: (value: string | string[] | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(
    private facilityDobryAgentService: DobryAgentFacilityService,
  ) {
  }

  ngOnInit(): void {
    this.facilityDobryAgentService.getSubCategoryDobryAgentSubject()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((items: SubCategory[]) => {
        this.items = (items ?? [])
          .filter((item) => !item.deleted)
          .filter((item) => !this.onlyActive || item.active)
          .sort((a, b) => {
            const firstPosition = a.position ?? 0;
            const secondPosition = b.position ?? 0;

            if (firstPosition !== secondPosition) {
              return firstPosition - secondPosition;
            }

            return a.name.localeCompare(b.name);
          });

        this.filteredItems = this.items;
      });
  }

  writeValue(value: string | string[] | null): void {
    this.value = value ?? (this.multiple ? [] : null);
  }

  registerOnChange(fn: (value: string | string[] | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  changeValue(value: string | string[] | null): void {
    this.value = value;
    this.onChange(value);
    this.onTouched();
  }

  compareByUuid(a: string | SubCategory, b: string | SubCategory): boolean {
    const first = typeof a === 'object' ? a?.uuid : a;
    const second = typeof b === 'object' ? b?.uuid : b;

    return first === second;
  }

  getOptionLabel(item: SubCategory): string {
    if (item.category?.name) {
      return `${item.name} / ${item.category.name}`;
    }

    return item.name;
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
